import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import {
  PLAN_LIMITS, type PlanTier, type PlanLimits, type EnterpriseTenant,
  withinLimit, resolveEnterpriseLimits, canUse as canUseFeature, type Feature,
} from '@/lib/planTiers';

/**
 * Returns the current user's plan tier and limits.
 *
 * For standard tiers: reads plan_tier from the profiles table.
 * For enterprise: resolves custom limits from the tenant config.
 * Falls back to 'free' for unauthenticated users.
 */
export function usePlan() {
  const { user, isAuthenticated } = useAuth();
  const [dbTier, setDbTier] = useState<PlanTier | null>(null);
  const [tenant, setTenant] = useState<EnterpriseTenant | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setDbTier(null);
      setTenant(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('profiles')
          .select('plan_tier')
          .eq('user_id', user.id)
          .maybeSingle();
        if (!cancelled && data?.plan_tier) {
          const tier = data.plan_tier as string;
          if (tier === 'enterprise' || tier in PLAN_LIMITS) {
            setDbTier(tier as PlanTier);
          }
        }
      } catch (err) {
        // Don't block UI; default tier kicks in via the useMemo below.
        // Log for support so a broken profiles-RLS policy is visible.
        logger.warn('usePlan: failed to fetch plan_tier', {
          userId: user.id,
          message: err instanceof Error ? err.message : String(err),
        });
      }

      // Check enterprise tenant membership
      try {
        const { data: tenantRows } = await supabase
          .from('workflows')
          .select('blocks')
          .eq('category', 'enterprise-tenant');

        if (!cancelled && tenantRows) {
          for (const row of tenantRows) {
            try {
              const config = (typeof row.blocks === 'string' ? JSON.parse(row.blocks) : row.blocks) as EnterpriseTenant;
              if (config.active && config.memberIds?.includes(user.id)) {
                setTenant(config);
                setDbTier('enterprise');
                break;
              }
            } catch { /* skip malformed configs */ }
          }
        }
      } catch (err) {
        logger.warn('usePlan: failed to fetch enterprise tenant config', {
          userId: user.id,
          message: err instanceof Error ? err.message : String(err),
        });
      }
    })();

    return () => { cancelled = true; };
  }, [user, isAuthenticated]);

  const tier: PlanTier = useMemo(() => {
    if (!isAuthenticated || !user) return 'free';
    if (dbTier) return dbTier;
    // While loading from DB, default to advanced for authenticated
    return 'advanced';
  }, [user, isAuthenticated, dbTier]);

  const limits: PlanLimits = useMemo(() => {
    if (tier === 'enterprise' && tenant) {
      return resolveEnterpriseLimits(tenant);
    }
    return PLAN_LIMITS[tier] ?? PLAN_LIMITS.free;
  }, [tier, tenant]);

  return {
    tier,
    limits,
    tenant,
    isAuthenticated,
    isPaid: tier !== 'free',
    isEnterprise: tier === 'enterprise',

    /** Check if user can add another block */
    canAddBlock: (currentCount: number) => withinLimit(currentCount, limits.maxBlocks),
    /** Check if user can add another connector */
    canAddConnector: (currentCount: number) => withinLimit(currentCount, limits.maxConnectors),
    /** Check if user can create another workspace */
    canCreateWorkspace: (currentCount: number) => withinLimit(currentCount, limits.maxWorkspaces),
    /** Check if user can nest deeper */
    canNestDeeper: (currentDepth: number) => withinLimit(currentDepth, limits.maxNestedLayers),
    /** Check if user can create more top-level groups */
    canAddTopGroup: (currentCount: number) => withinLimit(currentCount, limits.maxTopLevelGroups),
    /** Check if user can save to cloud */
    canSaveToCloud: (currentCount: number) => withinLimit(currentCount, limits.maxCloudStorage),
    /** Check if user can create co-op workspace */
    canCreateCoop: (currentCount: number) => withinLimit(currentCount, limits.maxCoopWorkspaces),
    /** Check if user can add another sheet/tab */
    canAddSheet: (currentCount: number) => withinLimit(currentCount, limits.maxSheets),
    /**
     * Typed feature-gate. Prefer this over raw `limits.canFoo` booleans
     * — the returned `requiredTier` lets UIs render "Upgrade to Pro to
     * unlock X" copy without a second lookup.
     */
    canUse: (feature: Feature) => canUseFeature(limits, feature),
  };
}
